import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useQuery } from '@apollo/client'
import { Form, Row, Col } from 'antd'
import useDebouncedFunction from 'utils/hooks/useDebouncedFunction'

import SeldonBeattieForm from 'model/wellness/seldonBeattie'
import { Label } from 'pages/Ticket/styled'
import FullScreenLoader from 'components/FullScreenLoader'
import Attachments from 'pages/Ticket/components/RequestData/components/Attachments'
import { Select, Button, DatePicker } from 'ui/styled'
import { DATE_FORMAT_YMD } from 'utils/helpers/dateHelper'
import SELDON_BEATTIE_INPUTS from 'apollo/queries/Wellness/SELDON_BEATTIE_INPUTS'
import useWellnessDeleteAttachment from 'apollo/mutations/Wellness/SELDON_BEATTIE_DELETE_ATTACHMENT'
import useWellnessUpdateAttachment from 'apollo/mutations/Wellness/SELDON_BEATTIE_UPDATE_ATTACHMENT'
import useWellnessUploadAttachment from 'apollo/mutations/Wellness/SELDON_BEATTIE_UPLOAD_ATTACHMENT'
import useWellnessFormUpdate from 'apollo/mutations/Wellness/SELDON_BEATTIE_UPDATE'
import NewAttachment from './components/NewAttachment'
import SeldonField from './SeldonBeattieFields/SeldonField'
import { FormItem } from './styled'

const { Item } = Form

const generateOptions = (array = []) =>
  array.map(i => ({
    value: i,
    label: i
  }))

const SeldonBeattieFields = ({
  form = {},
  seldonBeattie = false,
  disabled = false
}) => {
  const [newAttachment, setNewAttachment] = useState(null)
  const { data, loading } = useQuery(SELDON_BEATTIE_INPUTS)

  const { onWellnessAttachmentDelete } = useWellnessDeleteAttachment()
  const { onWellnessAttachmentUpdate } = useWellnessUpdateAttachment()
  const { onWellnessAttachmentUpload } = useWellnessUploadAttachment()
  const { onWellnessFormUpdate } = useWellnessFormUpdate()

  const attachToForm = async file => {
    if (!seldonBeattie) return false

    const { isErrorFree } = await onWellnessAttachmentUpload({
      id: seldonBeattie.id,
      attachment: {
        file,
        title: form.getFieldValue(['attachments', 0, 'title']),
        notes: form.getFieldValue(['attachments', 0, 'notes'])
      }
    })
    if (!isErrorFree) return false
    setNewAttachment(false)
    form.setFieldsValue({ attachments: null })
  }

  const onRemoveAttachment = () => {
    setNewAttachment(false)
    form.setFieldsValue({ attachments: [] })
  }

  const onAddAttachment = () => {
    setNewAttachment(true)
  }

  const updateSeldon = useCallback(
    async (key, value) => {
      if (!seldonBeattie) return false

      await onWellnessFormUpdate({
        id: seldonBeattie.id,
        attributes: { [key]: value }
      })
    },
    [seldonBeattie.id, onWellnessFormUpdate]
  )

  const debouncedUpdateSeldon = useDebouncedFunction(updateSeldon, 500)

  const attachUpdateHandler = async attachments => {
    if (!attachments || !attachments.length) return false

    await onWellnessAttachmentUpdate({
      id: attachments[0].id,
      attachment: {
        notes: attachments[0].notes,
        title: attachments[0].title
      }
    })
  }

  const attachRemoveHandler = async id => {
    await onWellnessAttachmentDelete({ id })
  }

  const onChangeDate = date => {
    const dateString = date.format(DATE_FORMAT_YMD)
    if (seldonBeattie) {
      debouncedUpdateSeldon('screen_date', dateString)
    }
  }

  if (loading || !data) return <FullScreenLoader />

  const {
    input_annual_physical_form: { smoking_attestation }
  } = data

  const options = {
    smoking_attestation: generateOptions(smoking_attestation.answers)
  }

  const { form_attachment } = seldonBeattie

  return (
    <>
      <Label>Wellness Form</Label>
      <Row gutter={[16, 0]}>
        <Col span={12}>
          <SeldonField
            name="patient_name"
            title="Patient Name"
            placeholder="Patient Name"
            seldonBeattie={seldonBeattie}
            updateFn={debouncedUpdateSeldon}
            required
            disabled={disabled}
          />
        </Col>
        <Col span={12}>
          <Label required>Screen Date</Label>
          <FormItem
            name="screen_date"
            rules={[
              {
                required: true,
                message: 'Please screen date'
              }
            ]}
          >
            <DatePicker
              format={DATE_FORMAT_YMD}
              onChange={onChangeDate}
              disabled={disabled}
            />
          </FormItem>
        </Col>
      </Row>

      <Row gutter={[16, 0]}>
        <Col span={12}>
          <SeldonField
            name="height"
            title="Height (in.)"
            placeholder="Height"
            type="number"
            seldonBeattie={seldonBeattie}
            updateFn={debouncedUpdateSeldon}
            disabled={disabled}
          />
        </Col>
        <Col span={12}>
          <SeldonField
            name="weight"
            title="Weight (lbs.)"
            placeholder="Weight"
            type="number"
            seldonBeattie={seldonBeattie}
            updateFn={debouncedUpdateSeldon}
            disabled={disabled}
          />
        </Col>
      </Row>

      <Row gutter={[16, 0]}>
        <Col span={12}>
          <SeldonField
            name="body_mass_index"
            title="BMI"
            placeholder="BMI"
            type="number"
            seldonBeattie={seldonBeattie}
            updateFn={debouncedUpdateSeldon}
            disabled={disabled}
          />
        </Col>
        <Col span={12}>
          <SeldonField
            name="a1c"
            title="A1c"
            placeholder="A1c"
            seldonBeattie={seldonBeattie}
            updateFn={debouncedUpdateSeldon}
            disabled={disabled}
          />
        </Col>
      </Row>

      <Row gutter={[16, 0]}>
        <Col span={12}>
          <SeldonField
            name="blood_pressure"
            title="Blood Pressure (mm Hg)"
            placeholder="Blood Pressure"
            seldonBeattie={seldonBeattie}
            updateFn={debouncedUpdateSeldon}
            disabled={disabled}
          />
        </Col>
        <Col span={12}>
          <SeldonField
            name="total_cholesterol"
            title="Total Cholesterol (mg/dL)"
            placeholder="Total Cholesterol"
            seldonBeattie={seldonBeattie}
            updateFn={debouncedUpdateSeldon}
            disabled={disabled}
          />
        </Col>
      </Row>

      <Row gutter={[16, 0]}>
        <Col span={12}>
          <SeldonField
            name="ldl_cholesterol"
            title="LDL Cholesterol (mg/dL)"
            placeholder="LDL Cholesterol"
            seldonBeattie={seldonBeattie}
            updateFn={debouncedUpdateSeldon}
            disabled={disabled}
          />
        </Col>
        <Col span={12}>
          <SeldonField
            name="hdl_cholesterol"
            title="HDL Cholesterol (mg/dL)"
            placeholder="HDL Cholesterol"
            seldonBeattie={seldonBeattie}
            updateFn={debouncedUpdateSeldon}
            disabled={disabled}
          />
        </Col>
      </Row>

      <Row gutter={[16, 0]}>
        <Col span={12}>
          <SeldonField
            name="triglycerides"
            title="Triglycerides (mg/dL)"
            placeholder="Triglycerides"
            seldonBeattie={seldonBeattie}
            updateFn={debouncedUpdateSeldon}
            disabled={disabled}
          />
        </Col>
        <Col span={12}>
          <SeldonField
            name="fasting_glucose"
            title="Fasting glucose (mg/dL)"
            placeholder="Fasting Glucose"
            seldonBeattie={seldonBeattie}
            updateFn={debouncedUpdateSeldon}
            disabled={disabled}
          />
        </Col>
      </Row>

      <Row gutter={[16, 0]}>
        <Col span={24}>
          <Label required>Have you used tobacco in the past 3 months?</Label>
          <Item
            name="smoking_attestation"
            rules={[
              {
                required: true,
                message: 'Please select an answer'
              }
            ]}
          >
            <Select
              options={options.smoking_attestation}
              placeholder={smoking_attestation?.placeholder}
              onChange={
                seldonBeattie
                  ? val => debouncedUpdateSeldon('smoking_attestation', val)
                  : undefined
              }
              disabled={disabled}
            />
          </Item>
        </Col>
      </Row>

      <Label required>Attachment</Label>
      {seldonBeattie && (
        <Attachments
          key={form_attachment?.id}
          attachments={[form_attachment]}
          updateHandler={attachUpdateHandler}
          removeHandler={attachRemoveHandler}
          disabled={disabled}
        />
      )}

      {newAttachment && (
        <NewAttachment
          key={newAttachment}
          index={0}
          attachHandler={attachToForm}
          onRemoveAttachment={onRemoveAttachment}
          isSingleFile
          disabled={disabled}
        />
      )}

      <Button
        onClick={onAddAttachment}
        disabled={!!newAttachment || form_attachment || disabled}
        data-test="ticketAddAttachmentBtn"
      >
        Add New Attachment
      </Button>
    </>
  )
}

SeldonBeattieFields.propTypes = {
  seldonBeattie: PropTypes.shape(SeldonBeattieForm.shape),
  form: PropTypes.object,
  disabled: PropTypes.bool
}

export default SeldonBeattieFields
